import React, { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Download } from 'lucide-react';
import DataTable from '../../components/ui/DataTable';
import Button from '../../components/ui/Button';
import { formatDateTime } from '../../utils/Utils';
import { getReportExportDownload } from '../../services/reportExportService';
import { useMyExports, formatBytes } from './config';

// Tek kaydırma: kapsayıcı sütun, tablo kalan alanı alır (min-height: 0).
const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
`;

const LoadFailed = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
  border-radius: 6px;
  padding: 10px 12px;
  font-size: 13px;
  margin-bottom: 12px;
`;

const STATUS_COLORS = {
  queued: { bg: '#f3f4f6', text: '#4b5563' },
  running: { bg: '#dbeafe', text: '#1d4ed8' },
  succeeded: { bg: '#dcfce7', text: '#15803d' },
  failed: { bg: '#fee2e2', text: '#b91c1c' },
};

const Badge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  background: ${({ $bg }) => $bg};
  color: ${({ $color }) => $color};
`;

const ErrorText = styled.div`
  color: #b91c1c;
  font-size: 12px;
  margin-top: 4px;
`;

// AKM POC §7.1 — "Dışa Aktarmalarım": arka planda hazırlanan raporlar; hazır olanı yalnız isteyen 7 gün indirir
// (imzalı, kısa ömürlü adres). EmployeesPage aynası: başlık yalnız DataTable'da.
export default function MyExportsPage() {
  const { t, i18n } = useTranslation();
  const { jobs, loading, failed } = useMyExports();
  const [downloading, setDownloading] = useState(null);

  const download = useCallback(async (job) => {
    setDownloading(job.id);
    try {
      const res = await getReportExportDownload(job.id);
      const url = res?.data?.status !== false ? res?.data?.data?.url : null;
      if (url) window.location.assign(url);
    } finally {
      setDownloading(null);
    }
  }, []);

  const reportName = useCallback((job) => (job.reportKey
    ? t(`reportExport.reports.${job.reportKey}`, { defaultValue: job.title || job.reportKey })
    : job.title || ''), [t]);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { display: 'excluded', filter: false, sort: false } },
    {
      name: 'reportKey', label: t('reportExport.myExports.report'),
      options: { filter: false, sort: false, customBodyRender: (v, meta) => reportName(jobs[meta.rowIndex] || { reportKey: v }) },
    },
    {
      name: 'format', label: t('reportExport.myExports.format'),
      options: { filter: false, sort: false, customBodyRender: (v) => (v ? t(`reportExport.${v}`, { defaultValue: v.toUpperCase() }) : '') },
    },
    {
      name: 'status', label: t('reportExport.myExports.status'),
      options: {
        filter: false,
        sort: false,
        customBodyRender: (v, meta) => {
          const colors = STATUS_COLORS[v] || STATUS_COLORS.queued;
          const job = jobs[meta.rowIndex];
          return (
            <>
              <Badge $bg={colors.bg} $color={colors.text}>{t(`reportExport.myExports.statuses.${v}`, { defaultValue: v })}</Badge>
              {v === 'failed' && job?.error ? (
                <ErrorText>{t(`responseErrors.${job.error}`, { defaultValue: t('responseErrors.reportExport.failed') })}</ErrorText>
              ) : null}
            </>
          );
        },
      },
    },
    {
      name: 'enqueuedAt', label: t('reportExport.myExports.enqueuedAt'),
      options: { filter: false, sort: false, customBodyRender: (v) => formatDateTime(v, i18n.language) },
    },
    {
      name: 'rowCount', label: t('reportExport.myExports.rows'),
      options: {
        filter: false,
        sort: false,
        customBodyRender: (v, meta) => {
          const rows = v ?? jobs[meta.rowIndex]?.estimatedRows;
          return rows === null || rows === undefined ? '-' : Number(rows).toLocaleString(i18n.language);
        },
      },
    },
    {
      name: 'resultFileSize', label: t('reportExport.myExports.size'),
      options: { filter: false, sort: false, customBodyRender: (v) => formatBytes(v, i18n.language) },
    },
    {
      name: 'resultExpiresAt', label: t('reportExport.myExports.expiresAt'),
      options: { filter: false, sort: false, customBodyRender: (v) => (v ? formatDateTime(v, i18n.language) : '-') },
    },
    {
      name: 'hasResult', label: ' ',
      options: {
        filter: false,
        sort: false,
        viewColumns: false,
        customBodyRender: (v, meta) => {
          const job = jobs[meta.rowIndex];
          if (!v || !job) return null;
          return (
            <Button
              type="button"
              variant="outline"
              data-action="download-export"
              loading={downloading === job.id}
              onClick={(e) => { e.stopPropagation(); download(job); }}
            >
              <Download size={16} aria-hidden="true" />
              {t('reportExport.myExports.download')}
            </Button>
          );
        },
      },
    },
  ], [t, i18n.language, jobs, reportName, downloading, download]);

  return (
    <Container>
      {failed ? <LoadFailed role="alert">{t('reportExport.myExports.loadFailed')}</LoadFailed> : null}
      <DataTable
        title={t('reportExport.myExports.title')}
        columns={columns}
        data={jobs}
        fetching={loading}
        serverSide={false}
        disableFilters
        download={false}
        pageSize={20}
        totalRowCount={jobs.length}
      />
    </Container>
  );
}
